/**
 * Font Registry — Single source of truth for all available fonts.
 * 
 * Used by: RightPanel dropdown, EPS export, Google Fonts loading.
 * Adding a new font? Just add an entry here — everything else picks it up automatically.
 */

export interface FontDefinition {
    /** CSS font-family name */
    family: string;
    /** Category for grouping in dropdown */
    category: 'sans-serif' | 'serif' | 'display' | 'monospace' | 'handwriting';
    /** Available font weights (for Google Fonts URL generation) */
    weights: number[];
    /** Whether this font is loaded from Google Fonts (vs system font) */
    googleFont: boolean;
    /** PostScript font name fallback for EPS export */
    psName: string;
}

/**
 * All available fonts — Google Fonts + system fonts.
 * Order within each category determines dropdown display order.
 */
export const FONT_REGISTRY: FontDefinition[] = [
    // ── Sans-Serif ──────────────────────────────────────────
    { family: 'Inter', category: 'sans-serif', weights: [400, 500, 600, 700], googleFont: true, psName: 'Helvetica' },
    { family: 'Roboto', category: 'sans-serif', weights: [400, 500, 700], googleFont: true, psName: 'Helvetica' },
    { family: 'Poppins', category: 'sans-serif', weights: [400, 500, 600, 700], googleFont: true, psName: 'Helvetica' },
    { family: 'Open Sans', category: 'sans-serif', weights: [400, 600, 700], googleFont: true, psName: 'Helvetica' },
    { family: 'Montserrat', category: 'sans-serif', weights: [400, 500, 600, 700], googleFont: true, psName: 'Helvetica' },
    { family: 'Lato', category: 'sans-serif', weights: [400, 700], googleFont: true, psName: 'Helvetica' },
    { family: 'Oswald', category: 'sans-serif', weights: [400, 500, 600, 700], googleFont: true, psName: 'Helvetica' },
    { family: 'Raleway', category: 'sans-serif', weights: [400, 500, 600, 700], googleFont: true, psName: 'Helvetica' },

    // ── Display ─────────────────────────────────────────────
    { family: 'Bebas Neue', category: 'display', weights: [400], googleFont: true, psName: 'Helvetica-Bold' },
    { family: 'Playfair Display', category: 'display', weights: [400, 700], googleFont: true, psName: 'Times-Roman' },
    { family: 'Archivo Black', category: 'display', weights: [400], googleFont: true, psName: 'Helvetica-Bold' },

    // ── Serif ───────────────────────────────────────────────
    { family: 'Merriweather', category: 'serif', weights: [400, 700], googleFont: true, psName: 'Times-Roman' },
    { family: 'Lora', category: 'serif', weights: [400, 700], googleFont: true, psName: 'Times-Roman' },
    { family: 'PT Serif', category: 'serif', weights: [400, 700], googleFont: true, psName: 'Times-Roman' },

    // ── Monospace ────────────────────────────────────────────
    { family: 'Roboto Mono', category: 'monospace', weights: [400, 700], googleFont: true, psName: 'Courier' },
    { family: 'Source Code Pro', category: 'monospace', weights: [400, 700], googleFont: true, psName: 'Courier' },

    // ── Handwriting / Script ────────────────────────────────
    { family: 'Dancing Script', category: 'handwriting', weights: [400, 700], googleFont: true, psName: 'Times-Roman' },
    { family: 'Pacifico', category: 'handwriting', weights: [400], googleFont: true, psName: 'Times-Roman' },
    { family: 'Great Vibes', category: 'handwriting', weights: [400], googleFont: true, psName: 'Times-Roman' },
    { family: 'Caveat', category: 'handwriting', weights: [400, 700], googleFont: true, psName: 'Times-Roman' },
    { family: 'Satisfy', category: 'handwriting', weights: [400], googleFont: true, psName: 'Times-Roman' },
    { family: 'Yellowtail', category: 'handwriting', weights: [400], googleFont: true, psName: 'Times-Roman' },
    { family: 'Kaushan Script', category: 'handwriting', weights: [400], googleFont: true, psName: 'Times-Roman' },
    { family: 'Shadows Into Light', category: 'handwriting', weights: [400], googleFont: true, psName: 'Times-Roman' },
    { family: 'Indie Flower', category: 'handwriting', weights: [400], googleFont: true, psName: 'Times-Roman' },
    { family: 'Permanent Marker', category: 'handwriting', weights: [400], googleFont: true, psName: 'Helvetica-Bold' },

    // ── System Fonts (always available, no loading needed) ──
    { family: 'Arial', category: 'sans-serif', weights: [400, 700], googleFont: false, psName: 'Helvetica' },
    { family: 'Helvetica', category: 'sans-serif', weights: [400, 700], googleFont: false, psName: 'Helvetica' },
    { family: 'Times New Roman', category: 'serif', weights: [400, 700], googleFont: false, psName: 'Times-Roman' },
    { family: 'Courier New', category: 'monospace', weights: [400, 700], googleFont: false, psName: 'Courier' },
    { family: 'Georgia', category: 'serif', weights: [400, 700], googleFont: false, psName: 'Times-Roman' },
];

/** Default font family for new text instances */
export const DEFAULT_FONT = 'Inter';

/** Category display labels for dropdown optgroup headers */
const CATEGORY_LABELS: Record<FontDefinition['category'], string> = {
    'sans-serif': 'Sans-Serif',
    'serif': 'Serif',
    'display': 'Display',
    'monospace': 'Monospace',
    'handwriting': 'Handwriting & Script',
};

/**
 * Get fonts grouped by category for dropdown rendering.
 * Returns an array of { label, fonts } in display order.
 */
export function getFontsByCategory(): { label: string; fonts: FontDefinition[] }[] {
    const categoryOrder: FontDefinition['category'][] = [
        'sans-serif', 'display', 'serif', 'monospace', 'handwriting',
    ];

    return categoryOrder
        .map(cat => ({
            label: CATEGORY_LABELS[cat],
            fonts: FONT_REGISTRY.filter(f => f.category === cat),
        }))
        .filter(group => group.fonts.length > 0);
}

/**
 * Get PostScript font name for EPS export.
 * Falls back to 'Helvetica' for unknown fonts.
 */
export function getPostScriptName(cssFamily: string): string {
    const normalized = cssFamily.toLowerCase().replace(/['"]/g, '').trim();
    const entry = FONT_REGISTRY.find(f => f.family.toLowerCase() === normalized);
    return entry?.psName || 'Helvetica';
}

/**
 * Build the Google Fonts CSS URL from registry.
 * Only includes fonts marked as googleFont: true.
 */
export function buildGoogleFontsUrl(): string {
    const googleFonts = FONT_REGISTRY.filter(f => f.googleFont);

    const families = googleFonts.map(f => {
        const name = f.family.replace(/ /g, '+');
        // For fonts with only weight 400, Google Fonts doesn't need :wght@
        if (f.weights.length === 1 && f.weights[0] === 400) {
            return `family=${name}`;
        }
        const weights = f.weights.sort((a, b) => a - b).join(';');
        return `family=${name}:wght@${weights}`;
    });

    return `https://fonts.googleapis.com/css2?${families.join('&')}&display=swap`;
}
